import { useMemo } from 'react';
import { Badge, Box, TextField, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from "../../../../i18n";
import FormlingContainer from "../../AgreementStudioModal/FormlingContainer/FormlingContainer";
import { getSystemVariableCategory, normalizeVariableLabel } from "../TemplateVariables.utils";
import { useTemplateVariables } from "../TemplateVariablesContext";
import styles from "./TemplateVariablesSection.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const GetTemplateVariablesSectionTID = parentTestId => getTestIds(parentTestId);
export function TemplateVariablesSection(_ref) {
  let {
    wasSubmitted,
    isOpen,
    setIsOpen,
    testId
  } = _ref;
  const {
    TemplateVariablesSectionTestIds
  } = useMemo(() => GetTemplateVariablesSectionTID(testId), [testId]);
  const {
    t
  } = useLocaleKeys('components.templateVariables.section');
  const {
    hasVariables,
    systemVariables,
    customVariables,
    resolvedValues,
    templateVariableValues,
    setTemplateVariableValue,
    unresolvedCustomVariables
  } = useTemplateVariables();
  const hasUnresolved = unresolvedCustomVariables.length > 0;
  const badgeText = hasUnresolved ? t.badgeNeedValues({
    count: unresolvedCustomVariables.length
  }) : t.badgeAllSet();
  const badgeSkin = hasUnresolved ? 'warning' : 'success';
  const hasCustomVariables = customVariables.length > 0;
  const explanatoryText = hasCustomVariables ? t.explanatoryText() : t.explanatoryTextSystemOnly();
  if (!hasVariables) {
    return null;
  }
  const headerBadge = /*#__PURE__*/_jsx(Badge, {
    skin: badgeSkin,
    testId: TemplateVariablesSectionTestIds.BadgeText(),
    children: badgeText
  });
  return /*#__PURE__*/_jsx("div", {
    className: styles.root,
    "data-testid": TemplateVariablesSectionTestIds.Root(),
    children: /*#__PURE__*/_jsx(FormlingContainer, {
      title: t.title(),
      togglePanelView: true,
      isOpen: isOpen,
      setIsOpen: setIsOpen,
      testId: testId,
      headerExtra: headerBadge,
      children: /*#__PURE__*/_jsxs(Box, {
        className: styles.sectionContent,
        children: [/*#__PURE__*/_jsx(Typography, {
          variant: "paragraph2",
          skin: "secondary",
          testId: TemplateVariablesSectionTestIds.ExplanatoryText(),
          children: explanatoryText
        }), systemVariables.length > 0 && /*#__PURE__*/_jsxs(Box, {
          className: styles.variablesGroup,
          testId: TemplateVariablesSectionTestIds.SystemVariablesGroup(),
          children: [/*#__PURE__*/_jsx(Typography, {
            variant: "paragraph2",
            weight: "bold",
            className: styles.groupTitle,
            children: t.systemVariablesGroup()
          }), systemVariables.map(label => {
            const resolved = resolvedValues[normalizeVariableLabel(label)];
            return /*#__PURE__*/_jsxs(Box, {
              className: styles.systemVariableRow,
              testId: TemplateVariablesSectionTestIds.SystemVariableRow(label),
              children: [/*#__PURE__*/_jsx(Typography, {
                variant: "paragraph2",
                weight: "bold",
                className: styles.variableLabel,
                children: label
              }), /*#__PURE__*/_jsx(Typography, {
                variant: "paragraph2",
                skin: resolved ? 'standard' : 'disabled',
                testId: TemplateVariablesSectionTestIds.SystemVariableValue(label),
                children: resolved || (getSystemVariableCategory(label) === 'Client' ? t.selectClientToResolve() : t.notYetAvailable())
              })]
            }, label);
          })]
        }), hasCustomVariables && /*#__PURE__*/_jsxs(Box, {
          className: styles.variablesGroup,
          testId: TemplateVariablesSectionTestIds.CustomVariablesGroup(),
          children: [/*#__PURE__*/_jsx(Typography, {
            variant: "paragraph2",
            weight: "bold",
            className: styles.groupTitle,
            children: t.customVariablesGroup()
          }), customVariables.map(label => {
            var _templateVariableValu;
            const value = (_templateVariableValu = templateVariableValues[normalizeVariableLabel(label)]) !== null && _templateVariableValu !== void 0 ? _templateVariableValu : '';
            const showError = wasSubmitted && unresolvedCustomVariables.includes(label);
            return /*#__PURE__*/_jsx(TextField, {
              label: label,
              value: value,
              onChange: e => setTemplateVariableValue(label, e.target.value),
              testId: TemplateVariablesSectionTestIds.CustomVariableField(label),
              error: showError,
              helperText: showError ? t.fieldError() : undefined
            }, normalizeVariableLabel(label));
          })]
        })]
      })
    })
  });
}